import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { webchatAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  MessageCircle, Globe, Plus, Copy, Check, RefreshCw,
  Shield, Code, ExternalLink, AlertTriangle, Loader2, X, Sparkles
} from 'lucide-react';
import { WebsiteChatNav } from '../components/WebsiteChatNav';

const TEASER_PRESETS = [
  "Hi there, have a question?",
  "Need help? Chat with us.",
  "Questions? We're here to help.",
];

export default function WebsiteChat() {
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [offlineMsg, setOfflineMsg] = useState('');
  const [brandColor, setBrandColor] = useState('#2563eb');
  const [teaserText, setTeaserText] = useState('');
  const [teaserEnabled, setTeaserEnabled] = useState(true);
  const [teaserDelay, setTeaserDelay] = useState(3);

  const loadWidget = useCallback(async () => {
    try {
      const res = await webchatAPI.getWidget();
      const w = res.data;
      setWidget(w);
      setWelcomeMsg(w.welcome_message || '');
      setOfflineMsg(w.offline_message || '');
      setBrandColor(w.brand_color || '#2563eb');
      setTeaserText(w.teaser_text || '');
      setTeaserEnabled(w.teaser_enabled !== false);
      setTeaserDelay(w.teaser_delay_seconds ?? 3);
    } catch (err) {
      toast.error('Failed to load widget config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadWidget(); }, [loadWidget]);

  const handleToggleEnabled = async (checked) => {
    if (checked && (!widget.allowed_domains || widget.allowed_domains.length === 0)) {
      toast.error('Add at least one allowed domain before enabling the widget');
      return;
    }
    setSaving(true);
    try {
      const res = await webchatAPI.updateWidget({ enabled: checked });
      setWidget(res.data);
      toast.success(checked ? 'Widget enabled' : 'Widget disabled');
    } catch (err) {
      toast.error('Failed to update widget');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const res = await webchatAPI.updateWidget({
        welcome_message: welcomeMsg,
        offline_message: offlineMsg,
        brand_color: brandColor,
        teaser_text: teaserText,
        teaser_enabled: teaserEnabled,
        teaser_delay_seconds: teaserDelay,
      });
      setWidget(res.data);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDomain = async () => {
    const domain = newDomain.trim();
    if (!domain) return;
    setAddingDomain(true);
    try {
      await webchatAPI.addDomain(domain);
      setNewDomain('');
      await loadWidget();
      toast.success(`Domain "${domain}" added`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to add domain';
      toast.error(msg);
    } finally {
      setAddingDomain(false);
    }
  };

  const handleRemoveDomain = async (domainId, domainName) => {
    try {
      await webchatAPI.removeDomain(domainId);
      await loadWidget();
      toast.success(`Domain "${domainName}" removed`);
    } catch (err) {
      toast.error('Failed to remove domain');
    }
  };

  const handleRegenerateKey = async () => {
    if (!window.confirm('Regenerate the widget key? This will break any existing embeds until you update the snippet.')) return;
    setRegenerating(true);
    try {
      await webchatAPI.regenerateKey();
      await loadWidget();
      toast.success('Widget key regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate key');
    } finally {
      setRegenerating(false);
    }
  };

  const embedSnippet = widget ? `<!-- Stock & Load Website Chat Widget -->
<script>
  (function() {
    var w = document.createElement('script');
    w.src = '${widget.widget_script_url}';
    w.setAttribute('data-widget-key', '${widget.widget_key}');
    w.async = true;
    document.head.appendChild(w);
  })();
</script>` : '';

  const copySnippet = () => {
    navigator.clipboard.writeText(embedSnippet);
    setCopied(true);
    toast.success('Snippet copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <Layout>
        <WebsiteChatNav />
        <div className="flex items-center justify-center min-h-[60vh]" data-testid="webchat-loading">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (!widget) {
    return (
      <Layout>
        <WebsiteChatNav />
        <div className="p-6 text-center text-muted-foreground" data-testid="webchat-error">
          Failed to load widget configuration. Please try again.
        </div>
      </Layout>
    );
  }

  const hasDomains = widget.allowed_domains && widget.allowed_domains.length > 0;

  return (
    <Layout>
      <WebsiteChatNav />
      <div className="p-4 sm:p-6 space-y-6" data-testid="webchat-setup-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-blue-600" />
              Website Chat
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Add a chat widget to your website to capture leads and talk to visitors in real time.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge
              variant={widget.enabled ? 'default' : 'secondary'}
              className={widget.enabled ? 'bg-emerald-600 hover:bg-emerald-600' : ''}
              data-testid="widget-status-badge"
            >
              {widget.enabled ? 'Live' : 'Disabled'}
            </Badge>
            <Switch
              checked={widget.enabled}
              onCheckedChange={handleToggleEnabled}
              disabled={saving}
              data-testid="widget-enable-toggle"
            />
          </div>
        </div>

        {/* Warning banner if no domains */}
        {!hasDomains && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="no-domains-warning">
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">No allowed domains configured</p>
              <p className="mt-0.5 text-amber-700">Add at least one domain below before enabling the widget. The chat will only work on pages from allowed domains.</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* Allowed Domains */}
            <Card data-testid="allowed-domains-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Allowed Domains
                </CardTitle>
                <CardDescription>
                  Only these domains can load your chat widget. Use <code className="text-xs bg-muted px-1 py-0.5 rounded">*.example.com</code> for wildcards.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g. www.yourcompany.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                    className="flex-1"
                    data-testid="domain-input"
                  />
                  <Button
                    onClick={handleAddDomain}
                    disabled={addingDomain || !newDomain.trim()}
                    size="sm"
                    data-testid="add-domain-btn"
                  >
                    {addingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    <span className="ml-1">Add</span>
                  </Button>
                </div>
                {hasDomains ? (
                  <div className="space-y-1.5" data-testid="domain-list">
                    {widget.allowed_domains.map((d) => (
                      <div key={d.id} className="flex items-center justify-between bg-muted/50 px-3 py-2 rounded-md" data-testid={`domain-row-${d.domain}`}>
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-mono">{d.domain}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-red-600"
                          onClick={() => handleRemoveDomain(d.id, d.domain)}
                          data-testid={`remove-domain-${d.domain}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">No domains added yet.</p>
                )}
              </CardContent>
            </Card>

            {/* Messages */}
            <Card data-testid="messages-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-600" />
                  Messages & Appearance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Welcome Message</label>
                  <Textarea
                    value={welcomeMsg}
                    onChange={(e) => setWelcomeMsg(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Hi! How can we help you today?"
                    data-testid="welcome-message-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{welcomeMsg.length}/500</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Offline Message</label>
                  <Textarea
                    value={offlineMsg}
                    onChange={(e) => setOfflineMsg(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="We're not available right now. Leave your info and we'll get back to you!"
                    data-testid="offline-message-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{offlineMsg.length}/500</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Brand Color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={brandColor}
                      onChange={(e) => setBrandColor(e.target.value)}
                      className="w-10 h-10 rounded-md border cursor-pointer"
                      data-testid="brand-color-input"
                    />
                    <Input
                      value={brandColor}
                      onChange={(e) => setBrandColor(e.target.value)}
                      maxLength={7}
                      className="w-28 font-mono text-sm"
                      data-testid="brand-color-text"
                    />
                    <div className="w-10 h-10 rounded-full border" style={{ backgroundColor: brandColor }} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bubble Teaser */}
            <Card data-testid="teaser-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    Bubble Teaser
                  </CardTitle>
                  <Switch
                    checked={teaserEnabled}
                    onCheckedChange={setTeaserEnabled}
                    data-testid="teaser-enable-toggle"
                  />
                </div>
                <CardDescription>
                  A small prompt near the chat bubble that draws attention. Dismissible by the visitor.
                </CardDescription>
              </CardHeader>
              {teaserEnabled && (
                <CardContent className="space-y-3">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Teaser Text</label>
                    <Input
                      value={teaserText}
                      onChange={(e) => setTeaserText(e.target.value)}
                      maxLength={200}
                      placeholder="Hi there, have a question?"
                      data-testid="teaser-text-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">{teaserText.length}/200</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {TEASER_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        className="text-xs px-2 py-1 rounded-full border border-muted-foreground/20 hover:border-blue-400 hover:text-blue-600 transition-colors"
                        onClick={() => setTeaserText(preset)}
                        data-testid={`teaser-preset-${preset.slice(0, 10)}`}
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Delay (seconds)</label>
                    <Input
                      type="number"
                      min={0}
                      max={30}
                      value={teaserDelay}
                      onChange={(e) => setTeaserDelay(parseInt(e.target.value) || 0)}
                      className="w-24"
                      data-testid="teaser-delay-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">How long before the teaser appears after page load</p>
                  </div>

                  {/* Live preview */}
                  <div className="mt-4">
                    <p className="text-xs font-medium text-muted-foreground mb-2">Preview</p>
                    <div className="flex items-end justify-end gap-2">
                      {teaserText && (
                        <div className="bg-white border shadow-lg rounded-xl rounded-br-sm px-3 py-2 text-sm max-w-[200px] animate-in fade-in slide-in-from-right-2 duration-300">
                          {teaserText}
                        </div>
                      )}
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: brandColor }}
                      >
                        <MessageCircle className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            <Button onClick={handleSaveSettings} disabled={saving} className="w-full" data-testid="save-settings-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save All Settings
            </Button>
          </div>

          {/* RIGHT COLUMN */}
          <div className="space-y-6">
            {/* Embed Snippet */}
            <Card data-testid="embed-snippet-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Code className="w-4 h-4 text-blue-600" />
                  Embed Snippet
                </CardTitle>
                <CardDescription>
                  Copy this code and paste it before the closing <code className="text-xs bg-muted px-1 py-0.5 rounded">&lt;/body&gt;</code> tag.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="relative">
                  <pre className="bg-zinc-950 text-zinc-200 p-4 rounded-lg text-xs leading-relaxed overflow-x-auto font-mono" data-testid="embed-snippet-code">
                    {embedSnippet}
                  </pre>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="absolute top-2 right-2"
                    onClick={copySnippet}
                    data-testid="copy-snippet-btn"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 text-sm space-y-2" data-testid="install-instructions">
                  <p className="font-medium flex items-center gap-1.5"><ExternalLink className="w-4 h-4" /> Quick Install Guide</p>
                  <ol className="list-decimal list-inside space-y-1 text-muted-foreground ml-1">
                    <li>Add your website domain to the <strong>Allowed Domains</strong> list</li>
                    <li>Copy the embed snippet above</li>
                    <li>Paste it into your website HTML before <code className="text-xs bg-background px-1 py-0.5 rounded">&lt;/body&gt;</code></li>
                    <li>Enable the widget using the toggle at the top</li>
                    <li>Visit your website to see the chat bubble</li>
                  </ol>
                </div>
              </CardContent>
            </Card>

            {/* Widget Key */}
            <Card data-testid="widget-key-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-600" />
                  Widget Key
                </CardTitle>
                <CardDescription>
                  Your public widget identifier. Safe to include in website code — only works on allowed domains.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <code className="bg-muted px-3 py-2 rounded-md text-sm font-mono flex-1 truncate" data-testid="widget-key-display">
                    {widget.widget_key}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateKey}
                    disabled={regenerating}
                    data-testid="regenerate-key-btn"
                  >
                    {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Regenerating invalidates existing embeds. You'll need to update the snippet.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
